import {
  dispatchOfflineServiceWorkerUpdateAvailable,
  shouldAnnounceOfflineServiceWorkerUpdate,
} from '@core/domain/offlineServiceWorkerUpdateEvents.ts';
import { Capacitor } from '@capacitor/core';

function announceOfflineServiceWorkerUpdateIfNeeded(): void {
  const sw = globalThis.navigator?.serviceWorker;
  if (!sw) return;
  if (!shouldAnnounceOfflineServiceWorkerUpdate(sw.controller != null)) return;
  dispatchOfflineServiceWorkerUpdateAvailable();
}

/** After SW activate: only announce when this page already had a controller before registration. */
export function handleOfflineServiceWorkerActivatedMessage(hadControllerBeforeRegistration: boolean): void {
  if (!hadControllerBeforeRegistration) return;
  dispatchOfflineServiceWorkerUpdateAvailable();
}

function watchInstallingWorker(worker: ServiceWorker): void {
  if (worker.state === 'installed') {
    announceOfflineServiceWorkerUpdateIfNeeded();
    return;
  }
  worker.addEventListener('statechange', () => {
    if (worker.state === 'installed') announceOfflineServiceWorkerUpdateIfNeeded();
  });
}

/** Listens for a waiting worker and new registrations; does not auto-reload the page. */
export function bindOfflineServiceWorkerUpdateAnnouncements(reg: ServiceWorkerRegistration): void {
  if (reg.waiting) announceOfflineServiceWorkerUpdateIfNeeded();

  reg.addEventListener('updatefound', () => {
    const installing = reg.installing;
    if (installing) watchInstallingWorker(installing);
  });
}

function offlineServiceWorkerRegisterUrl(baseUrl: string, buildRevision: string): string {
  const rev = buildRevision.trim();
  if (!rev) return baseUrl;
  const sep = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${sep}rev=${encodeURIComponent(rev)}`;
}

/** Registers the offline shell service worker (no-op when disabled or unavailable). */
export async function registerOfflineServiceWorkerFromEnv(env: {
  ADMIN_OFFLINE_SERVICE_WORKER: string;
  ADMIN_OFFLINE_SW_URL: string;
  ADMIN_OFFLINE_SW_SCOPE: string;
  ADMIN_OFFLINE_SW_READY_TIMEOUT_MS: string;
  ADMIN_BUILD_REVISION: string;
}): Promise<void> {
  const sw = globalThis.navigator?.serviceWorker;
  if (!sw || env.ADMIN_OFFLINE_SERVICE_WORKER === '0' || Capacitor.isNativePlatform()) return;

  const hadControllerBeforeRegistration = sw.controller != null;
  sw.addEventListener('message', (event) => {
    if (event.data?.type === 'BONO_SW_ACTIVATED') {
      handleOfflineServiceWorkerActivatedMessage(hadControllerBeforeRegistration);
    }
  });

  const base = (env.ADMIN_OFFLINE_SW_URL ?? '').trim() || '/offline-sw.js';
  const swUrl = offlineServiceWorkerRegisterUrl(base, env.ADMIN_BUILD_REVISION ?? '');
  const swScope = (env.ADMIN_OFFLINE_SW_SCOPE ?? '').trim() || '/';
  const readyMs = Math.max(
    0,
    Math.floor(Number.parseInt((env.ADMIN_OFFLINE_SW_READY_TIMEOUT_MS ?? '').trim() || '2500', 10) || 2500),
  );
  try {
    const reg = await sw.register(swUrl, { scope: swScope, updateViaCache: 'none' });
    bindOfflineServiceWorkerUpdateAnnouncements(reg);
    void reg.update().catch(() => {
      /* optional */
    });
    await Promise.race([sw.ready, new Promise<void>((r) => globalThis.setTimeout(r, readyMs))]);
  } catch {
    /* file://, CSP */
  }
}
